import type { ReactNode } from 'react';

import type { IconProps } from '@bestseller-bit/frontend-community.theme.buying-theme';
import type { BoxProps } from '@mui/material/Box';
import type { IconButtonProps } from '@mui/material/IconButton';
import type { LinearProgressProps } from '@mui/material/LinearProgress';
import type { SnackbarProps } from '@mui/material/Snackbar';
import type { StackProps } from '@mui/material/Stack';
import type { TypographyProps } from '@mui/material/Typography';

// Enum for easily working with snackbar types types
enum SnackbarTypes {
    info = 'info',
    success = 'success',
    warning = 'warning',
    error = 'error',
}

/**
 * @description Interface for the configuration of a snackbar
 * @param id - The id of the snackbar
 * @param title - The title of the snackbar
 * @param message - The message of the snackbar
 * @param type - The type of the snackbar, "info" by default
 * @param icon - Can be used to overwrite the icon of the snackbar
 * @param autoClose - Whether the snackbar should close automatically or not
 * @param autoCloseDuration - If auto closing, overwrite the duration in milliseconds
 * @param isVisible - Whether the snackbar is visible or not
 * @param slotProps - Can be used to overwrite the props of the snackbar components, usefull for custom styling
 */
interface BuyingSnackbarConfig {
    id: string;
    title: string;
    message: string | ReactNode;
    type?: keyof typeof SnackbarTypes;
    icon?: ReactNode;
    autoClose?: boolean;
    autoCloseDuration?: number;
    isVisible?: boolean;
    hideCloseButton?: boolean;
    actions?:
        | Array<ReactNode>
        | ((props: { handleSnackbarClose: () => void }) => Array<ReactNode>);
    disableShadow?: boolean;
    slotProps?: {
        snackbar?: SnackbarProps;
        container?: StackProps;
        iconContainer?: BoxProps;
        icon?: IconProps;
        textContainer?: StackProps;
        title?: TypographyProps;
        message?: TypographyProps;
        actionsContainer?: StackProps;
        closeButton?: IconButtonProps;
        linearProgress?: LinearProgressProps;
    };
    onClose?: () => void;
}

// Type for the state of the snackbar reducer, which is an array of snackbar configurations
type SnackbarReducerState = Array<BuyingSnackbarConfig>;

// Type for the actions that can be dispatched to the snackbar reducer
type SnackbarReducerAction =
    | { type: 'add'; payload: Omit<BuyingSnackbarConfig, 'id'> }
    | { type: 'remove'; payload: { id: string } };

const snackbarReducer = (
    state: SnackbarReducerState,
    action: SnackbarReducerAction
): SnackbarReducerState => {
    switch (action.type) {
        case 'add': {
            // Check if a snackbar with the same title and message already exists
            const isSnackbarDuplicate = state.some(
                (snackbar) =>
                    snackbar.title === action.payload.title &&
                    snackbar.message === action.payload.message
            );
            //if the same snackbar is already visible, do not add it again
            if (isSnackbarDuplicate) {
                return state;
            }
            //otherwise add the new snackbar
            return [...state, { ...action.payload, id: crypto.randomUUID() }];
        }
        case 'remove':
            return state.filter((s) => s.id !== action.payload.id);
        default:
            return state;
    }
};

export { snackbarReducer, SnackbarTypes };
export type {
    BuyingSnackbarConfig,
    SnackbarReducerAction,
    SnackbarReducerState,
};
